import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useContext } from 'react'
import AppContext from '../../../context/appContext'
import { formDefinitionQueryOptions } from '../../../hooks/data/useFormDefinitions'
import { refDataQueryOptions } from '../../../hooks/data/useRefData'
import { samplingAreasQueryOptions } from '../../../hooks/data/useSamplingAreas'
import { loadMonitoringActivities } from '../../../services/farmMapping/monitoring/monitoringActivities/loadMonitoringActivities'
import type { Farm, FieldDefinition, SOCSampleWithDocuments } from '../../../types/apiModels'
import { monitoringWorkflowsTypes } from '../../../utils/constants'
import { downloadFile } from '../../../utils/downloadFile'
import { buildSOCSampleExport, type SelectOption } from './buildSOCSampleExport'
import { writeSOCSampleWorkbook } from './writeSOCSampleWorkbook'

// Laboratory and SOC protocol have no ref-data table: their labels only live in the form
// definition's static select options.
const selectOptionsOf = (fields: FieldDefinition[], name: string): SelectOption[] =>
  (fields.find(field => field.name === name) as { defaultOptions?: SelectOption[] })
    ?.defaultOptions ?? []

export const useDownloadSOCSample = (farm: Farm) => {
  const queryClient = useQueryClient()
  const { programConfig } = useContext(AppContext)
  const formDefinitionId = programConfig?.formDefinitions?.monitoringSOCSample?.id

  return useMutation({
    mutationFn: async (sample: SOCSampleWithDocuments) => {
      if (!formDefinitionId) {
        throw new Error('No se encontró la definición del formulario de resultados de suelos')
      }

      const [activities, strata, dapMethods, soilTextures, samplingNumbers, formDefinition] =
        await Promise.all([
          // No task-status filter, unlike the dialog's select: a saved row must resolve its
          // activity even after that activity's tasks changed status.
          loadMonitoringActivities({
            farmId: farm.id,
            monitoringWorkflowId: [
              monitoringWorkflowsTypes.SOC,
              monitoringWorkflowsTypes.SOCPOA,
              monitoringWorkflowsTypes.SOCV2,
              monitoringWorkflowsTypes.SOCRETERRA,
            ],
            size: 1000,
          }),
          queryClient.ensureQueryData(samplingAreasQueryOptions(farm.id)),
          queryClient.ensureQueryData(refDataQueryOptions('RefDAPMethod')),
          queryClient.ensureQueryData(refDataQueryOptions('RefSoilTextures')),
          queryClient.ensureQueryData(refDataQueryOptions('RefSamplingNumber')),
          queryClient.ensureQueryData(formDefinitionQueryOptions(formDefinitionId)),
        ])

      const exportData = buildSOCSampleExport(sample, {
        farm: { name: farm.name, hubName: farm.Hub?.name },
        activities: activities.items,
        strata,
        dapMethods,
        soilTextures,
        samplingNumbers,
        laboratories: selectOptionsOf(formDefinition.fields, 'laboratoryId'),
        socProtocols: selectOptionsOf(formDefinition.fields, 'socProtocolId'),
      })

      downloadFile({
        fileName: exportData.fileName,
        file: await writeSOCSampleWorkbook(exportData),
      })
    },
  })
}
